import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { AuthErrorCode, UserRole } from '@kfolks/types'
import { AUTH_QUERY_KEY } from '@/auth/AuthProvider'
import { consumePostLoginRedirect } from '@/auth/post-login-redirect'
import { authService } from '@/services/auth.service'

/**
 * Landing page after Entra ID redirects back with ?code=xxx.
 *
 * Flow:
 *   1. Read `code` from query params
 *   2. POST to /auth/callback — exchanges code for the HttpOnly cookie
 *   3. On success: navigate to dashboard (seeding AuthProvider's cache)
 *   4. On error: navigate to /login?error=auth_failed
 */
const RETRY_DELAY_MS = 500

/**
 * Decide whether an /auth/callback failure is worth retrying once.
 *
 * Only retry on transient symptoms that map onto Lambda cold-start before
 * the request reaches Microsoft's token endpoint: no response (network
 * error, DNS, API Gateway 502/504) or 5xx from our API. Do NOT retry 401 —
 * that means the authorization code was already redeemed at Microsoft on
 * the first attempt (codes are single-use), and a retry will deterministically
 * fail with invalid_grant. Do NOT retry ACCOUNT_INACTIVE — permanent
 * business-rule rejection.
 */
function isRetryable(err: unknown): boolean {
	if (!isAxiosError(err)) return false
	const data = err.response?.data as { error?: { code?: string } } | undefined
	if (data?.error?.code === AuthErrorCode.ACCOUNT_INACTIVE) return false

	const status = err.response?.status
	// Network/no-response failures: retry — request never landed at Entra.
	if (status === undefined) return true
	// 5xx from our API: cold-start init failure before the Entra exchange ran.
	return status >= 500
}

/**
 * Call /auth/callback once; on a transient cold-start failure retry exactly
 * once after a short delay. See isRetryable() for which failures qualify.
 */
async function exchangeCodeWithRetry(code: string): Promise<Awaited<ReturnType<typeof authService.handleCallback>>> {
	try {
		return await authService.handleCallback(code)
	} catch (err) {
		if (!isRetryable(err)) throw err
		await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))
		return authService.handleCallback(code)
	}
}

export function AuthCallbackPage() {
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	// Prevent double-firing in React 18 strict mode
	const handled = useRef(false)

	useEffect(() => {
		if (handled.current) return
		handled.current = true

		const code = searchParams.get('code')
		const error = searchParams.get('error')

		if (error) {
			void navigate(`/login?error=${encodeURIComponent(error)}`, { replace: true })
			return
		}

		if (!code) {
			void navigate('/login?error=missing_code', { replace: true })
			return
		}

		// One-shot retry on transient cold-start failures — see
		// exchangeCodeWithRetry() and isRetryable() above.
		exchangeCodeWithRetry(code)
			.then((user) => {
				// Seed AuthProvider's React Query cache with the freshly-authenticated
				// viewer. AuthProvider mounted above the router skipped /auth/me on the
				// callback route because the cookie wasn't set yet — without this seed
				// the user lands on /dashboard authenticated server-side but
				// `useAuth().user` stays null until a full reload.
				queryClient.setQueryData(AUTH_QUERY_KEY, user)
				// Deep-link respect: if the user was bounced here from a protected
				// route via /login, send them back to the original URL. Otherwise
				// fall back to the role default.
				// Spec 0 BR0-002: USER / ADMIN / SUPER_ADMIN land on /dashboard.
				// VIEWER (and any unexpected role) stays on / — the existing app home.
				const pendingRedirect = consumePostLoginRedirect()
				if (pendingRedirect) {
					void navigate(pendingRedirect, { replace: true })
					return
				}
				const dashboardRoles: readonly string[] = [UserRole.USER, UserRole.ADMIN, UserRole.SUPER_ADMIN]
				const target = dashboardRoles.includes(user?.role ?? '') ? '/dashboard' : '/'
				void navigate(target, { replace: true })
			})
			.catch((err: unknown) => {
				const data = isAxiosError(err) ? (err.response?.data as { error?: { code?: string } } | undefined) : undefined
				if (data?.error?.code === AuthErrorCode.ACCOUNT_INACTIVE) {
					void navigate('/error/inactive', { replace: true })
					return
				}
				void navigate('/login?error=auth_failed', { replace: true })
			})
	}, [navigate, queryClient, searchParams])

	return (
		<div className="min-h-screen flex items-center justify-center">
			<div className="text-center space-y-4">
				<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-foreground mx-auto" />
				<p className="text-sm text-muted-foreground">Completing sign in…</p>
			</div>
		</div>
	)
}
